import { useState, type ReactNode } from 'react';
import { auditApi, type AuditLogRow } from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { formatDateTime, humanize } from '@/lib/format';
import { adminNavItems } from '@/pages/navItems';

interface AuditFilters {
  action: string;
  entityType: string;
  q: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: AuditFilters = { action: '', entityType: '', q: '', from: '', to: '' };

/** `billing.invoice_run_create` → "Billing · Invoice run create". */
function actionLabel(action: string): string {
  const [area, ...rest] = action.split('.');
  if (!rest.length) return humanize(area);
  return `${humanize(area)} · ${humanize(rest.join(' '))}`;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function displayValue(v: unknown): ReactNode {
  if (v === undefined) return <span className="text-muted">—</span>;
  if (v === null) return <span className="text-muted">empty</span>;
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  return <code className="audit-json">{JSON.stringify(v, null, 2)}</code>;
}

function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Field-level rows for a before/after pair. Unchanged fields are skipped when both sides exist. */
function diffRows(before: unknown, after: unknown): Array<{ field: string; before: unknown; after: unknown }> {
  if (!isPlainObject(before) && !isPlainObject(after)) {
    if (before === undefined && after === undefined) return [];
    return [{ field: 'Value', before, after }];
  }
  const b = isPlainObject(before) ? before : {};
  const a = isPlainObject(after) ? after : {};
  const keys = Array.from(new Set([...Object.keys(b), ...Object.keys(a)]));
  const bothSides = isPlainObject(before) && isPlainObject(after);
  return keys
    .filter((k) => !bothSides || !sameValue(b[k], a[k]))
    .filter((k) => k !== 'updatedAt' || !bothSides)
    .map((k) => ({ field: k, before: b[k], after: a[k] }));
}

function fieldLabel(key: string): string {
  return humanize(key.replace(/([a-z0-9])([A-Z])/g, '$1_$2'));
}

/** yyyy-mm-dd (Manila) → ISO instant at start/end of that Manila day. */
function manilaDayBoundary(date: string, end: boolean): string | undefined {
  if (!date) return undefined;
  const d = new Date(`${date}T${end ? '23:59:59.999' : '00:00:00'}+08:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export function AdminAudit() {
  const [draft, setDraft] = useState<AuditFilters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<AuditFilters>(EMPTY_FILTERS);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<AuditLogRow | null>(null);
  const limit = 50;

  const { data, loading, error, reload } = useAsync(
    () =>
      auditApi.list({
        action: applied.action || undefined,
        entityType: applied.entityType || undefined,
        q: applied.q || undefined,
        from: manilaDayBoundary(applied.from, false),
        to: manilaDayBoundary(applied.to, true),
        limit,
        offset,
      }),
    [applied, offset],
  );

  const applyFilters = (next: AuditFilters) => {
    setOffset(0);
    setApplied({ ...next });
  };

  const hasFilters = Object.values(applied).some(Boolean);
  const dirty = JSON.stringify(draft) !== JSON.stringify(applied);

  const columns = [
    {
      key: 'createdAt',
      header: 'When',
      render: (row: AuditLogRow) => formatDateTime(row.createdAt),
    },
    {
      key: 'actor',
      header: 'Who',
      value: (row: AuditLogRow) => row.actorEmail ?? '',
      render: (row: AuditLogRow) => row.actorEmail ?? (row.actorId ? 'Unknown user' : 'System'),
    },
    {
      key: 'action',
      header: 'Action',
      render: (row: AuditLogRow) => actionLabel(row.action),
    },
    {
      key: 'entity',
      header: 'Record',
      value: (row: AuditLogRow) => row.entityType,
      render: (row: AuditLogRow) => humanize(row.entityType),
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (row: AuditLogRow) => row.reason ?? '—',
    },
  ];

  const changes = selected ? diffRows(selected.before, selected.after) : [];
  const metadataEntries = selected?.metadata ? Object.entries(selected.metadata) : [];

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Audit log</h1>
            <p className="text-muted">Every sensitive change, who made it and when. Click a row to see what changed.</p>
          </div>
        </div>

        <form
          className="row"
          style={{ gap: 'var(--space-md)', marginBottom: 'var(--space-lg)', flexWrap: 'wrap', alignItems: 'flex-end' }}
          onSubmit={(e) => {
            e.preventDefault();
            applyFilters(draft);
          }}
        >
          <Input
            id="audit-action"
            label="Action"
            value={draft.action}
            onChange={(e) => setDraft({ ...draft, action: e.target.value })}
            placeholder="e.g. billing"
          />
          <Input
            id="audit-entity"
            label="Record type"
            value={draft.entityType}
            onChange={(e) => setDraft({ ...draft, entityType: e.target.value })}
            placeholder="e.g. trip"
          />
          <Input
            id="audit-search"
            label="Search"
            value={draft.q}
            onChange={(e) => setDraft({ ...draft, q: e.target.value })}
            placeholder="Text in the change"
          />
          <Input
            id="audit-from"
            label="From"
            type="date"
            value={draft.from}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })}
          />
          <Input
            id="audit-to"
            label="To"
            type="date"
            value={draft.to}
            onChange={(e) => setDraft({ ...draft, to: e.target.value })}
          />
          <Button type="submit" variant={dirty ? 'primary' : 'secondary'}>Apply filters</Button>
          {hasFilters && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setDraft(EMPTY_FILTERS);
                applyFilters(EMPTY_FILTERS);
              }}
            >
              Clear
            </Button>
          )}
        </form>

        <DataTable
          columns={columns}
          data={data?.rows ?? []}
          keyField="id"
          loading={loading}
          loadError={error ? `Could not load the audit log: ${error}` : null}
          onRetry={() => void reload()}
          dense
          filterSort={false}
          onRowClick={(row) => setSelected(row)}
          emptyMessage={
            hasFilters
                ? 'No audit events match these filters.'
                : 'No audit events yet.'
          }
        />

        <div className="row-between" style={{ marginTop: 'var(--space-md)' }}>
          <span className="text-muted">
            {data && data.total > 0
              ? `${Math.min(offset + 1, data.total)}–${Math.min(offset + limit, data.total)} of ${data.total}`
              : ''}
          </span>
          <div className="row" style={{ gap: 'var(--space-sm)' }}>
            <Button
              type="button"
              variant="secondary"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={!data || offset + limit >= data.total}
              onClick={() => setOffset(offset + limit)}
            >
              Next
            </Button>
          </div>
        </div>

        <Modal
          open={selected !== null}
          onClose={() => setSelected(null)}
          title={selected ? actionLabel(selected.action) : 'Audit event'}
          size="lg"
        >
          {selected && (
            <div>
              <dl className="audit-meta">
                <dt>When</dt>
                <dd>{formatDateTime(selected.createdAt)}</dd>
                <dt>Who</dt>
                <dd>
                  {selected.actorEmail ?? (selected.actorId ? 'Unknown user' : 'System')}
                  {selected.actorRole ? ` (${humanize(selected.actorRole)})` : ''}
                </dd>
                <dt>Record</dt>
                <dd>{humanize(selected.entityType)}</dd>
                {selected.reason && (
                  <>
                    <dt>Reason</dt>
                    <dd>{selected.reason}</dd>
                  </>
                )}
                {selected.source && (
                  <>
                    <dt>Source</dt>
                    <dd>{humanize(selected.source)}</dd>
                  </>
                )}
              </dl>

              <h3 style={{ margin: 'var(--space-lg) 0 var(--space-sm)' }}>What changed</h3>
              {changes.length ? (
                <div className="audit-diff-wrap">
                  <table className="audit-diff">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Before</th>
                        <th>After</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changes.map((c) => (
                        <tr key={c.field}>
                          <td>{c.field === 'Value' ? c.field : fieldLabel(c.field)}</td>
                          <td>{displayValue(c.before)}</td>
                          <td>{displayValue(c.after)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted">No field-level changes were recorded for this event.</p>
              )}

              {metadataEntries.length > 0 && (
                <>
                  <h3 style={{ margin: 'var(--space-lg) 0 var(--space-sm)' }}>Details</h3>
                  <dl className="audit-meta">
                    {metadataEntries.map(([k, v]) => (
                      <div key={k} style={{ display: 'contents' }}>
                        <dt>{fieldLabel(k)}</dt>
                        <dd>{displayValue(v)}</dd>
                      </div>
                    ))}
                  </dl>
                </>
              )}
            </div>
          )}
        </Modal>
      </div>
      <style>{`
        .audit-meta {
          display: grid;
          grid-template-columns: max-content 1fr;
          gap: var(--space-xs) var(--space-md);
          font-size: 0.875rem;
          margin: 0;
        }
        .audit-meta dt { color: var(--color-text-muted); }
        .audit-meta dd { margin: 0; overflow-wrap: anywhere; }
        .audit-diff-wrap { overflow-x: auto; }
        .audit-diff {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.8125rem;
        }
        .audit-diff th, .audit-diff td {
          text-align: left;
          vertical-align: top;
          padding: var(--space-xs) var(--space-sm);
          border-bottom: 1px solid var(--color-border);
          overflow-wrap: anywhere;
        }
        .audit-diff th { font-weight: 600; }
        .audit-json {
          display: block;
          white-space: pre-wrap;
          font-size: 0.75rem;
          max-height: 12rem;
          overflow: auto;
        }
      `}</style>
    </Layout>
  );
}
